import type { z } from "zod";
import { ValidationError } from "@/shared/lib/errors";
import { logger } from "@/shared/lib/logger";

/**
 * Result type for JSON parsing with schema validation
 */
export type ParseJsonWithSchemaResult<T> =
	| { success: true; data: T }
	| { success: false; error: string; zodError?: z.ZodError };

/**
 * Safely parse JSON string with Zod schema validation
 *
 * This function provides type-safe JSON parsing by validating the parsed
 * JSON against a Zod schema. It ensures that all JSON parsing operations
 * are validated and typed correctly.
 *
 * @param json - The JSON string to parse
 * @param schema - Zod schema to validate the parsed JSON against
 * @returns A result object with either success=true and validated data, or success=false with an error message
 *
 * @example
 * ```typescript
 * const schema = z.object({ name: z.string(), age: z.number() });
 * const result = parseJsonWithSchema('{"name":"John","age":30}', schema);
 * if (result.success) {
 *   console.log(result.data.name); // Type-safe access
 * } else {
 *   console.error(result.error);
 * }
 * ```
 */
export function parseJsonWithSchema<T>(
	json: string,
	schema: z.ZodSchema<T>,
): ParseJsonWithSchemaResult<T> {
	try {
		// Parse JSON - this can throw if JSON is malformed
		const parsed: unknown = JSON.parse(json);

		// Validate against schema
		const validationResult = schema.safeParse(parsed);

		if (validationResult.success) {
			return { success: true, data: validationResult.data };
		}

		// Validation failed
		return {
			success: false,
			error: `Validation failed: ${validationResult.error.message}`,
			zodError: validationResult.error,
		};
	} catch (error) {
		// JSON parsing failed
		const errorMessage = error instanceof Error ? error.message : String(error);
		return {
			success: false,
			error: `JSON parse error: ${errorMessage}`,
		};
	}
}

/**
 * Parse JSON with schema validation, throwing on error
 *
 * Use this when you want errors to throw instead of returning a result object.
 * Useful in contexts where validation failures should be handled by error boundaries.
 *
 * @param json - The JSON string to parse
 * @param schema - Zod schema to validate the parsed JSON against
 * @returns The validated and typed data
 * @throws {Error} If JSON parsing fails or validation fails
 *
 * @example
 * ```typescript
 * try {
 *   const data = parseJsonWithSchemaOrThrow('{"name":"John"}', schema);
 *   // data is fully typed
 * } catch (error) {
 *   // Handle error
 * }
 * ```
 */
export function parseJsonWithSchemaOrThrow<T>(
	json: string,
	schema: z.ZodSchema<T>,
): T {
	const result = parseJsonWithSchema(json, schema);
	if (result.success) {
		return result.data;
	}
	if (result.zodError) {
		// Convert ZodError to field errors manually since Zod v4 has PropertyKey[] paths
		const fieldErrors = result.zodError.issues.map((issue) => ({
			field: issue.path.map(String).join("."),
			message: issue.message,
		}));
		throw new ValidationError("Validation failed", fieldErrors);
	}
	throw new ValidationError(result.error);
}

/**
 * Parse JSON with schema validation, returning default value on error
 *
 * Use this when you want to gracefully handle parsing/validation errors
 * by falling back to a default value instead of throwing.
 *
 * @param json - The JSON string to parse
 * @param schema - Zod schema to validate the parsed JSON against
 * @param defaultValue - Default value to return if parsing or validation fails
 * @param context - Optional context string for error logging
 * @returns The validated and typed data, or defaultValue if parsing/validation fails
 *
 * @example
 * ```typescript
 * const data = parseJsonWithSchemaOrDefault(
 *   '{"name":"John"}',
 *   schema,
 *   { name: "Unknown" },
 *   "UserProfile"
 * );
 * ```
 */
export function parseJsonWithSchemaOrDefault<T>(
	json: string,
	schema: z.ZodSchema<T>,
	defaultValue: T,
	context?: string,
): T {
	const result = parseJsonWithSchema(json, schema);
	if (result.success) {
		return result.data;
	}
	if (context) {
		logger.warn(`[${context}] Failed to parse/validate JSON, using default`, {
			error: result.error,
			zodError: result.zodError,
		});
	}
	return defaultValue;
}
